import { Effect, put, takeLeading, call, all, takeLatest } from 'redux-saga/effects'

import { Site } from 'generated/mos/structure'
import { AttributeRef } from 'generated/mos/entity'
import { EventPointOfInterest, GenericPointOfInterest, StopPointOfInterest, VisitorType, Stop, Tour, TourCategory, CarouselItem, Mood } from 'generated/mos/org/getty/audio'
import { VisitorInfoPage, Amenity } from 'generated/mos/org/getty/visitorinfo'

import {
  GetStopRequest,
  GetStopResponse,
  ListStopsRequest,
  ListStopsResponse,
  GetTourRequest,
  GetTourResponse,
  GetMoodRequest,
  GetMoodResponse,
  ListToursRequest,
  ListToursResponse,
  ListMoodsRequest,
  ListMoodsResponse,
  ListVisitorTypesRequest,
  ListVisitorTypesResponse,
  GetVisitorTypeRequest,
  GetVisitorTypeResponse,
  ListTourCategoriesResponse,
  ListTourCategoriesRequest,
  ListCarouselItemsRequest,
  ListCarouselItemsResponse,
  GetCarouselItemRequest,
  GetCarouselItemResponse,
  GetStopPointOfInterestRequest,
  GetStopPointOfInterestResponse,
  ListStopPointsOfInterestRequest,
  ListStopPointsOfInterestResponse,
  GetEventPointOfInterestRequest,
  GetEventPointOfInterestResponse,
  ListEventPointsOfInterestRequest,
  ListEventPointsOfInterestResponse,
  GetGenericPointOfInterestRequest,
  GetGenericPointOfInterestResponse,
  ListGenericPointsOfInterestRequest,
  ListGenericPointsOfInterestResponse,
} from 'generated/mos/org/getty/audioaccess'
import {
  CreateStopRequest,
  CreateStopResponse,
  DeleteStopRequest,
  DeleteStopResponse,
  UpdateStopRequest,
  UpdateStopResponse,
  CreateTourRequest,
  CreateTourResponse,
  DeleteTourRequest,
  DeleteTourResponse,
  UpdateTourRequest,
  UpdateTourResponse,
  CreateMoodRequest,
  CreateMoodResponse,
  DeleteMoodRequest,
  DeleteMoodResponse,
  UpdateMoodRequest,
  UpdateMoodResponse,
  CreateTourCategoryRequest,
  CreateTourCategoryResponse,
  DeleteTourCategoryRequest,
  DeleteTourCategoryResponse,
  UpdateTourCategoryRequest,
  UpdateTourCategoryResponse,
  UpdateTourCategoriesRequest,
  UpdateTourCategoriesResponse,
  CreateCarouselItemResponse,
  CreateCarouselItemRequest,
  UpdateCarouselItemRequest,
  UpdateCarouselItemResponse,
  CreateStopPointOfInterestRequest,
  CreateStopPointOfInterestResponse,
  UpdateStopPointOfInterestRequest,
  UpdateStopPointOfInterestResponse,
  DeleteStopPointOfInterestRequest,
  DeleteStopPointOfInterestResponse,
  CreateEventPointOfInterestRequest,
  CreateEventPointOfInterestResponse,
  UpdateEventPointOfInterestRequest,
  UpdateEventPointOfInterestResponse,
  DeleteEventPointOfInterestRequest,
  DeleteEventPointOfInterestResponse,
  CreateGenericPointOfInterestRequest,
  CreateGenericPointOfInterestResponse,
  UpdateGenericPointOfInterestRequest,
  UpdateGenericPointOfInterestResponse,
  DeleteGenericPointOfInterestRequest,
  DeleteGenericPointOfInterestResponse,
  CreateVisitorTypeRequest,
  CreateVisitorTypeResponse,
  UpdateVisitorTypeRequest,
  UpdateVisitorTypeResponse,
  DeleteVisitorTypeRequest,
  DeleteVisitorTypeResponse,
} from 'generated/mos/org/getty/audiomanagement'
import {
  Getty360Event,
  ListGetty360EventsRequest,
  ListGetty360EventsResponse
} from 'generated/mos/org/getty/integration'
import {
  ListReleasesRequest,
  ListReleasesResponse,
  ListBundleSummariesRequest,
  ListBundleSummariesResponse,
  ListBundlesRequest,
  ListBundlesResponse,
} from 'generated/mos/org/getty/publishaccess'
import {
  CreateReleaseRequest,
  CreateReleaseResponse,
} from 'generated/mos/org/getty/publishmanagement'
import {
  ListValidationFailuresRequest,
  ListValidationFailuresResponse,
} from 'generated/mos/org/getty/validationaccess'
import {
  ListVisitorInfoSiteContentsRequest,
  ListVisitorInfoSiteContentsResponse,
  ListVisitorInfoPagesRequest,
  ListVisitorInfoPagesResponse,
  ListAmenitiesRequest,
  ListAmenitiesResponse,
  GetVisitorInfoPageRequest,
  GetVisitorInfoPageResponse,
  GetAmenityRequest,
  GetAmenityResponse,
  ListWayfindingMapsRequest,
  ListWayfindingMapsResponse,
} from 'generated/mos/org/getty/visitorinfoaccess'
import {
  CreateVisitorInfoPageRequest,
  CreateVisitorInfoPageResponse,
  DeleteVisitorInfoPageRequest,
  DeleteVisitorInfoPageResponse,
  DeleteAmenityRequest,
  DeleteAmenityResponse,
  CreateAmenityRequest,
  CreateAmenityResponse,
  UpdateVisitorInfoPageRequest,
  UpdateVisitorInfoPageResponse,
  UpdateAmenityRequest,
  UpdateAmenityResponse,
  UpdateVisitorInfoSiteContentRequest,
  UpdateVisitorInfoSiteContentResponse,
} from 'generated/mos/org/getty/visitorinfomanagement'
import {
  GetTMSObjectRequest,
  GetTMSObjectResponse,
  ListTMSObjectsRequest,
  ListTMSObjectsResponse,
} from 'generated/mos/org/getty/integrationtms'
import {
  GetLinkedFieldMappingsRequest,
  GetLinkedFieldMappingsResponse,
} from 'generated/mos/linkedfieldmappings'
import {
  ResolveLinkableFieldValuesRequest,
  ResolveLinkableFieldValuesResponse,
} from 'generated/mos/linkedfieldresolver'
import { BuildingLevel } from 'generated/mos/structure'
import {
  GetSpaceRequest,
  GetSpaceResponse,
  ListBuildingLevelsRequest,
  ListBuildingLevelsResponse,
  ListSitesRequest,
  ListSitesResponse,
} from 'generated/mos/structureaccess'
import { Page } from 'generated/mos/pagination'
import { Placement, SpacePlacement } from 'generated/mos/placement'

import { Invariant, isNotNullOrUndefined, pick } from 'helpers/core'
import { extractSourceAttributeNames, resolveAttributeRefsOfEntityList } from 'helpers/linkables'
import { captureException } from 'helpers/logging'
import { failureStatusMessage, unaryGRPC } from 'helpers/sagas'

import { GettyActions, actionCreators, GettyGetTourSuccess, GettyUpdateTourSuccess, GettyGetMoodSuccess, GettyUpdateMoodSuccess, GettyUpdateEventPointOfInterestSuccess, GettyUpdateStopPointOfInterestSuccess } from './actions'
import { stopSchema } from './schemas/stop'
import { pointOfInterestSchema } from './schemas/point-of-interest'
import { ListMediaRequest, ListMediaResponse } from 'generated/mos/mediaaccess';
import { Media } from 'generated/mos/media';
import { audioCarouselItemValueSchema, imageCarouselItemValueSchema, videoCarouselItemValueSchema } from './schemas/carousel-item';

import { bffEndpoint, StatusCode } from 'helpers/bff'
import { assemblePayload, ensureValidSession } from 'helpers/sagas'
import { AmentityUpdate, AnalyticsResponse } from './store'

const gettyTakeLeading = <TActionKey extends keyof GettyActions>(
  pattern: TActionKey,
  worker: (action: GettyActions[TActionKey]) => any,
) => {
  return takeLeading(pattern, worker);
}

// used in searching to ensure the latest search term is used
const gettyTakeLatest = <TActionKey extends keyof GettyActions>(
  pattern: TActionKey,
  worker: (action: GettyActions[TActionKey]) => any,
) => {
  return takeLatest(pattern, worker);
}

const getStopSaga = gettyTakeLeading(
  'gettyGetStopRequest',
  function* (action) {
    try {
      const { payload } = action
      let stopTours: ListToursResponse.Entity | undefined
      let stopMoods: ListMoodsResponse.Entity | undefined
      const response: GetStopResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetStop',
        payload,
        GetStopRequest.codec,
        GetStopResponse.codec,
      )

      // no stop returned.
      if(!response.stop) {
        throw new Invariant('[ getStopSaga ] Missing `stop` in response payload')
      }

      // get tours which contain the stop.
      if(response.stop.tourRefs) {
        stopTours = yield* unaryGRPC(
          'mos.org.getty.audio_access.AudioAccess/ListTours',
          {
            ...ListToursRequest.defaults,
            filter: {
              type: 'mos.org.getty.audio_access.ListToursRequest.ByRef' as const,
              tourRefs: response.stop.tourRefs,
            }
          },
          ListToursRequest.codec,
          ListToursResponse.codec,
        )
      }

      if(response.stop.moodRefs) {
        stopMoods = yield* unaryGRPC(
          'mos.org.getty.audio_access.AudioAccess/ListMoods',
          {
            ...ListMoodsRequest.defaults,
            filter: {
              type: 'mos.org.getty.audio_access.ListMoodsRequest.ByRef' as const,
              moodRefs: response.stop.moodRefs,
            }
          },
          ListMoodsRequest.codec,
          ListMoodsResponse.codec,
        )
      }

      // @ts-ignore
      // get related stops.
      const relatedStops = yield getStopsByRef(response.stop.relatedStopRefs)

      // if the stop has a base entity, fetch the entity, field mappings and resolved values.
      if (response.stop.basedOnRef) {
        let baseEntity: GetTMSObjectResponse.Entity | undefined

        const { basedOnRef } = response.stop
        // check based on entity type.
        if (basedOnRef.typename === 'mos.org.getty.integration_tms.TMSObject') {
          // get the tms object the stop was based on.
          baseEntity = yield* unaryGRPC(
            'mos.org.getty.integration_tms.IntegrationTMSAccess/GetTMSObject',
            {
              type: 'mos.org.getty.integration_tms.GetTMSObjectRequest',
              tmsObjectRef: {
                typename: 'mos.org.getty.integration_tms.TMSObject',
                id: basedOnRef.id,
              }
            },
            GetTMSObjectRequest.codec,
            GetTMSObjectResponse.codec,
          )
        }

        // get field mappings available to the entity type.
        const fieldMappings: GetLinkedFieldMappingsResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_mappings.LinkedFieldMappings/GetLinkedFieldMappings',
          {
            type: 'mos.linked_field_mappings.GetLinkedFieldMappingsRequest',
            sourceTypename: 'mos.org.getty.audio.Stop' as const,
            targetTypename: basedOnRef.typename,
            relationshipName: 'based_on',
            sourceAttributeNames: extractSourceAttributeNames(Stop.defaults),
          },
          GetLinkedFieldMappingsRequest.codec,
          GetLinkedFieldMappingsResponse.codec,
        )

        // only store valid mappings.
        const mappings = fieldMappings.mappings.filter(mapping => mapping.targetAttributeNames.length)

        const targetFieldRefs = mappings.reduce((acc: ReadonlyArray<any>, mapping) => {
          const attributeRefs: ReadonlyArray<AttributeRef.Entity> = mapping.targetAttributeNames
            .map(attributeName => ({
              type: 'mos.entity.AttributeRef',
              typename: basedOnRef.typename,
              id: basedOnRef.id,
              attributeName,
            }))

          return [
            ...acc,
            ...attributeRefs,
          ]
        }, [])

        // resolve attribute refs.
        const resolvedValues: ResolveLinkableFieldValuesResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_resolver.LinkedFieldResolver/ResolveLinkableFieldValues',
          {
            type: ResolveLinkableFieldValuesRequest.refName,
            targetFieldRefs,
          },
          ResolveLinkableFieldValuesRequest.codec,
          ResolveLinkableFieldValuesResponse.codec,
        )

        yield put(actionCreators.gettyGetStopSuccess(
          response.stop,
          stopTours && stopTours.tours || undefined,
          relatedStops,
          // extend this as other base entities become available.
          baseEntity && baseEntity.tmsObject || undefined,
          mappings,
          resolvedValues.resolvedValueResults,
          stopMoods && stopMoods.moods || undefined,
        ))

      } else {
        yield put(actionCreators.gettyGetStopSuccess(
          response.stop,
          stopTours && stopTours.tours || undefined,
          relatedStops
        ))
      }
    } catch (error) {
      yield put(actionCreators.gettyGetStopFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

function* getStopsByRef(stopRefs: ReadonlyArray<Stop.Ref>) {
  if(!stopRefs) return

  if (!stopRefs.length) return [];
  const payload: ListStopsRequest.Entity = {
    ...ListStopsRequest.defaults,
    page: {
      ...Page.defaults,
      pageSize: 10 ** 9,
    },
    filter: {
      ...ListStopsRequest.ListStopFilter.defaults,
      byRefs: stopRefs,
    },
  };
  const response: ListStopsResponse.Entity = yield* unaryGRPC(
    'mos.org.getty.audio_access.AudioAccess/ListStops',
    payload,
    ListStopsRequest.codec,
    ListStopsResponse.codec,
  );

  // reorder output to ensure that the order matches the input
  const stopIds = stopRefs.map(ref => ref.id);
  const stops = response.stops.slice().sort((requestItem, responseItem) =>
    stopIds.indexOf(requestItem.ref ? requestItem.ref.id : '') - stopIds.indexOf(responseItem.ref ? responseItem.ref.id : '')
  );

  const resolvedStops: ReadonlyArray<Stop.Entity> =
    yield* resolveAttributeRefsOfEntityList<Stop.Entity>(stops, stopSchema)

  return resolvedStops
}

const getTourSaga = gettyTakeLeading(
  'gettyGetTourRequest',
  function* (action) {
    try {
      const response: GetTourResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetTour',
        action.payload,
        GetTourRequest.codec,
        GetTourResponse.codec,
      );
      if (!response.tour) {
        throw new Invariant('[ getTourSaga ] Missing `tour` in response payload')
      }
      const stops: ReadonlyArray<Stop.Entity> = yield getStopsByRef(response.tour.stopRefs);

      const responseWithStops: GettyGetTourSuccess = {
        ...response,
        tour: { ...response.tour, stops },
      }

      yield put(actionCreators.gettyGetTourSuccess(responseWithStops));
    } catch (error) {
      yield put(actionCreators.gettyGetTourFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const getMoodSaga = gettyTakeLeading(
  'gettyGetMoodRequest',
  function* (action) {
    try {
      const response: GetMoodResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetMood',
        action.payload,
        GetMoodRequest.codec,
        GetMoodResponse.codec,
      );
      if (!response.mood) {
        throw new Invariant('[ getMoodSaga ] Missing `mood` in response payload')
      }

      const stopPointsOfInterest: ReadonlyArray<StopPointOfInterest.Entity> = yield getStopPointsOfInterestByRef(response.mood.stopPointsOfInterestRefs);
      const genericPointsOfInterest: ReadonlyArray<GenericPointOfInterest.Entity> = yield getGenericPointsOfInterestByRef(response.mood.genericPointsOfInterestRefs);
      const eventPointsOfInterest: ReadonlyArray<EventPointOfInterest.Entity> = yield getEventPointsOfInterestByRef(response.mood.eventPointsOfInterestRefs);

      const responseWithStopPointsOfInterest: GettyGetMoodSuccess = {
        ...response,
        mood: { ...response.mood, stopPointsOfInterest, genericPointsOfInterest, eventPointsOfInterest },
      }
      yield put(actionCreators.gettyGetMoodSuccess(responseWithStopPointsOfInterest));
    } catch (error) {
      yield put(actionCreators.gettyGetMoodFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

function* getSpace(spaceRef: GetSpaceRequest.Entity['spaceRef']) {
  // TODO: With this function running in parallel what's the best way to handle
  // errors? We want to attempt all requests, and (prob) need to keep error messages.
  const payload: GetSpaceRequest.Entity = {
    type: GetSpaceRequest.refName,
    spaceRef,
  }
  const response: GetSpaceResponse.Entity = yield* unaryGRPC(
    'mos.structure_access.StructureAccess/GetSpace',
    payload,
    GetSpaceRequest.codec,
    GetSpaceResponse.codec,
  );
  return response;
}

const listStopsSaga = gettyTakeLeading(
  'gettyListStopsRequest',
  listStops)

// same as above but using takeLatest. used when searching
const listStopsSagaLatest = gettyTakeLatest(
  'gettyListStopsSearchRequest',
  listStops
)

function* listStops(action: GettyActions['gettyListStopsRequest' | 'gettyListStopsSearchRequest']) {
  try {
    const { payload, cumulativePaginationEnabled } = action

    const response: ListStopsResponse.Entity = yield* unaryGRPC(
      'mos.org.getty.audio_access.AudioAccess/ListStops',
      payload,
      ListStopsRequest.codec,
      ListStopsResponse.codec,
    )
    const resolvedStops: ReadonlyArray<Stop.Entity> =
      yield* resolveAttributeRefsOfEntityList<Stop.Entity>(response.stops, stopSchema)

    const fieldsWithDefaults = {
      title: "primary_title",
      artworkName: "primary_title",
      producedBy: "produced_by",
      placeCreated: "place_created",
      dateCreated: "date_created",
      materials: "materials",
      creditLine: "credit_line",
      image: "image_media_ref",
      imageCopyright: "image_copyright",
      placement: "placement",
      objectDescription: "object_description",
      objectCopyright: "object_copyright"
    }

    const hasAtLeastOneOverride = (innerObject: any): boolean => {
      if (innerObject && 
        'basedOnRef' in innerObject &&
        innerObject.basedOnRef === undefined
      ) {
          return false
      }

      for (const [key, value] of Object.entries(fieldsWithDefaults)) {

        if (key === "imageCopyright" || key === "objectCopyright" || key === "image" || key === "placement"){
          if ((innerObject as any) && (innerObject as any)[key] &&
            (innerObject as any)[key].value &&
            (innerObject as any)[key].value.attributeName !== value) {
            return true
          }
          return false
        }

        const enUsTranslation = (innerObject as any) &&
          (innerObject as any)[key] &&
          (innerObject as any)[key].translatedValues &&
          (innerObject as any)[key].translatedValues.find(
            (tv: any) => tv.language &&
            tv.language.languageCode === 'en' && 
            tv.language.variant === 'US'
          );

        const customValue = enUsTranslation &&
          enUsTranslation.linkableStringValue &&
          enUsTranslation.linkableStringValue.stringValue ||
          (typeof enUsTranslation.linkableStringValue.value === 'string' &&
            enUsTranslation.linkableStringValue.value) || 
          undefined;

        const alternateValue = enUsTranslation && 
          enUsTranslation.linkableStringValue && (
            (enUsTranslation.linkableStringValue.linkTargetFieldRef &&
             enUsTranslation.linkableStringValue.linkTargetFieldRef.attributeName) ||
            (enUsTranslation.linkableStringValue.value &&
             enUsTranslation.linkableStringValue.value.attributeName) ||
            undefined
          );

        if (customValue || alternateValue && alternateValue !== value){ 
          return true; 
        }

      }
      return false
    }

    for (const [key, innerObject] of Object.entries(response.stops)) {
      (resolvedStops as any)[key]['hasAtLeastOneOverride'] = hasAtLeastOneOverride(innerObject);
    }

    // Get spaces in parallel (make unique to avoid unnecessary requests)
    const refsToFetch = [...new Set(resolvedStops.map(stop => {
      return stop.placement && stop.placement.value as Placement.Entity
    }))];
    const spaceResponses: GetSpaceResponse.Entity[] = yield all(
      refsToFetch
        .filter(isNotNullOrUndefined)
        .filter((p: Placement.Entity) => {
          const space = p.placement as SpacePlacement.Entity
          return !p && !space
        })
        .map((p: Placement.Entity) => {
          const space = p.placement as SpacePlacement.Entity
          return call(getSpace, space.spaceRef)
        })
    );

    // Nest spaces into response
    const nestedStops = resolvedStops.map(stop => {
      const matchingResponse = spaceResponses.find(res => {
        const placement = stop.placement && stop.placement.value as Placement.Entity
        const stopSpace = placement && placement.placement as SpacePlacement.Entity
        return !!res.space && res.space.ref && stopSpace && res.space.ref === stopSpace.spaceRef
      });
      return matchingResponse ? { ...stop, space: matchingResponse.space } : stop;
    });

    const nestedResponse = {
      ...response, stops: nestedStops, cumulativePaginationEnabled: cumulativePaginationEnabled
    }
    yield put(actionCreators.gettyListStopsSuccess(nestedResponse))

  } catch (error) {
    yield put(actionCreators.gettyListStopsFailure(
      failureStatusMessage(error.message)
    ))
  }
}

const createStopSaga = gettyTakeLeading(
  'gettyCreateStopRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: CreateStopResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateStop',
        payload,
        CreateStopRequest.codec,
        CreateStopResponse.codec,
      )

      yield put(actionCreators.gettyCreateStopSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyCreateStopFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateStopSaga = gettyTakeLeading(
  'gettyUpdateStopRequest',
  function* (action: GettyActions['gettyUpdateStopRequest']) {
    try {
      const { payload } = action
      const response: UpdateStopResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateStop',
        payload,
        UpdateStopRequest.codec,
        UpdateStopResponse.codec,
      )

      if(!response.stop) {
        throw new Invariant('[ updateStopSaga ] Missing `stop` in response payload')
      }

      // @ts-ignore
      const relatedStops = yield getStopsByRef(response.stop.relatedStopRefs)
      yield put(actionCreators.gettyUpdateStopSuccess(response.stop, relatedStops))

    } catch (error) {
      yield put(actionCreators.gettyUpdateStopFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const deleteStopSaga = gettyTakeLeading(
  'gettyDeleteStopRequest',
  function* (action: GettyActions['gettyDeleteStopRequest']) {
    const { payload } = action
    try {
      const response: DeleteStopResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteStop',
        payload,
        DeleteStopRequest.codec,
        DeleteStopResponse.codec,
      )

      yield put(actionCreators.gettyDeleteStopSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyDeleteStopFailure(
        Stop.mustRef(payload.stopRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

const createTourSaga = gettyTakeLeading(
  'gettyCreateTourRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: CreateTourResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateTour',
        payload,
        CreateTourRequest.codec,
        CreateTourResponse.codec,
      )

      yield put(actionCreators.gettyCreateTourSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyCreateTourFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const createMoodSaga = gettyTakeLeading(
  'gettyCreateMoodRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: CreateMoodResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateMood',
        payload,
        CreateMoodRequest.codec,
        CreateMoodResponse.codec,
      )

      yield put(actionCreators.gettyCreateMoodSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyCreateMoodFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const deleteTourSaga = gettyTakeLeading(
  'gettyDeleteTourRequest',
  function* (action) {
    const { payload } = action
    try {
      const response: DeleteTourResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteTour',
        payload,
        DeleteTourRequest.codec,
        DeleteTourResponse.codec,
      )

      yield put(actionCreators.gettyDeleteTourSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyDeleteTourFailure(
        Tour.mustRef(payload.tourRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

const deleteMoodSaga = gettyTakeLeading(
  'gettyDeleteMoodRequest',
  function* (action) {
    const { payload } = action
    try {
      const response: DeleteMoodResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteMood',
        payload,
        DeleteMoodRequest.codec,
        DeleteMoodResponse.codec,
      )

      yield put(actionCreators.gettyDeleteMoodSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyDeleteMoodFailure(
        Mood.mustRef(payload.moodRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateTourSaga = gettyTakeLeading(
  'gettyUpdateTourRequest',
  function* (action: GettyActions['gettyUpdateTourRequest']) {
    try {
      const { payload } = action
      const response: UpdateTourResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateTour',
        payload,
        UpdateTourRequest.codec,
        UpdateTourResponse.codec,
      );
      if (!response.tour) {
        throw new Invariant('[ updateTourSaga ] Missing `tour` in response payload')
      }
      const stops: ReadonlyArray<Stop.Entity> = yield getStopsByRef(response.tour.stopRefs);

      const responseWithStops: GettyUpdateTourSuccess = {
        ...response,
        tour: { ...response.tour, stops },
      }

      yield put(actionCreators.gettyUpdateTourSuccess(responseWithStops));

    } catch (error) {
      yield put(actionCreators.gettyUpdateTourFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const updateMoodSaga = gettyTakeLeading(
  'gettyUpdateMoodRequest',
  function* (action: GettyActions['gettyUpdateMoodRequest']) {
    try {
      const { payload } = action
      const response: UpdateMoodResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateMood',
        payload,
        UpdateMoodRequest.codec,
        UpdateMoodResponse.codec,
      );
      if (!response.mood) {
        throw new Invariant('[ updateMoodSaga ] Missing `mood` in response payload')
      }
      const stopPointsOfInterest: ReadonlyArray<StopPointOfInterest.Entity> = yield getStopPointsOfInterestByRef(response.mood.stopPointsOfInterestRefs);
      const genericPointsOfInterest: ReadonlyArray<GenericPointOfInterest.Entity> = yield getGenericPointsOfInterestByRef(response.mood.genericPointsOfInterestRefs);
      const eventPointsOfInterest: ReadonlyArray<EventPointOfInterest.Entity> = yield getEventPointsOfInterestByRef(response.mood.eventPointsOfInterestRefs);

      const responseWithStopPointsOfInterest: GettyUpdateMoodSuccess = {
        ...response,
        mood: { ...response.mood, stopPointsOfInterest, genericPointsOfInterest, eventPointsOfInterest },
      }

      yield put(actionCreators.gettyUpdateMoodSuccess(responseWithStopPointsOfInterest));
    } catch (error) {
      yield put(actionCreators.gettyUpdateMoodFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const listToursSaga = gettyTakeLeading(
  'gettyListToursRequest',
  function* (action) {
    try {
      const response: ListToursResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListTours',
        action.payload,
        ListToursRequest.codec,
        ListToursResponse.codec,
      );
      yield put(actionCreators.gettyListToursSuccess(response));

    } catch (error) {
      yield put(actionCreators.gettyListToursFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const listMoodsSaga = gettyTakeLeading(
  'gettyListMoodsRequest',
  function* (action) {
    try {
      const response: ListMoodsResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListMoods',
        action.payload,
        ListMoodsRequest.codec,
        ListMoodsResponse.codec,
      );
      yield put(actionCreators.gettyListMoodsSuccess(response));

    } catch (error) {
      yield put(actionCreators.gettyListMoodsFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const listVisitorTypesSaga = gettyTakeLeading(
  'gettyListVisitorTypesRequest',
  function* (action) {
    try {
      const response: ListVisitorTypesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListVisitorTypes',
        action.payload,
        ListVisitorTypesRequest.codec,
        ListVisitorTypesResponse.codec,
      );
      yield put(actionCreators.gettyListVisitorTypesSuccess(response));

    } catch (error) {
      yield put(actionCreators.gettyListVisitorTypesFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const getVisitorTypeSaga = gettyTakeLeading(
  'gettyGetVisitorTypeRequest',
  function* (action) {
    try {
      const response: GetVisitorTypeResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetVisitorType',
        action.payload,
        GetVisitorTypeRequest.codec,
        GetVisitorTypeResponse.codec,
      );
      if (!response.visitorType) {
        throw new Invariant('[ getVisitorTypeSaga ] Missing `visitorType` in response payload')
      }
      yield put(actionCreators.gettyGetVisitorTypeSuccess(
        response.visitorType,
      ))
    } catch (error) {
      yield put(actionCreators.gettyGetVisitorTypeFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const createVisitorTypeSaga = gettyTakeLeading(
  'gettyCreateVisitorTypeRequest',
  function* (action) {
    try {
      const { payload } = action

      if (!payload.label) {
        throw new Invariant('[ createVisitorTypeSaga ] Missing `label` in payload')
      }

      if (!payload.tag) {
        throw new Invariant('[ createVisitorTypeSaga ] Missing `tag` in payload')
      }

      const response: CreateVisitorTypeResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateVisitorType',
        { ...CreateVisitorTypeRequest.defaults, label: payload.label, tag: payload.tag },
        CreateVisitorTypeRequest.codec,
        CreateVisitorTypeResponse.codec,
      )

      yield put(actionCreators.gettyCreateVisitorTypeSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyCreateVisitorTypeFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateVisitorTypeSaga = gettyTakeLeading(
  'gettyUpdateVisitorTypeRequest',
  function* (action: GettyActions['gettyUpdateVisitorTypeRequest']) {
    try {
      const { payload } = action
      const response: UpdateVisitorTypeResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateVisitorType',
        payload,
        UpdateVisitorTypeRequest.codec,
        UpdateVisitorTypeResponse.codec,
      );
      if (!response.visitorType) {
        throw new Invariant('[ updateVisitorTypeSaga ] Missing `visitorType` in response payload')
      }

      yield put(actionCreators.gettyUpdateVisitorTypeSuccess(response.visitorType));
    } catch (error) {
      yield put(actionCreators.gettyUpdateVisitorTypeFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const deleteVisitorTypeSaga = gettyTakeLeading(
  'gettyDeleteVisitorTypeRequest',
  function* (action: GettyActions['gettyDeleteVisitorTypeRequest']) {
    const { payload } = action
    try {
      const response: DeleteVisitorTypeResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteVisitorType',
        payload,
        DeleteVisitorTypeRequest.codec,
        DeleteVisitorTypeResponse.codec,
      )

      yield put(actionCreators.gettyDeleteVisitorTypeSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyDeleteVisitorTypeFailure(
        VisitorType.mustRef(payload.visitorTypeRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)


// using takeLatest as this saga is only used when in searching TMS objects
const listTMSObjectsSaga = gettyTakeLatest(
  'gettyListTMSObjectsRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: ListTMSObjectsResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.integration_tms.IntegrationTMSAccess/ListTMSObjects',
        payload,
        ListTMSObjectsRequest.codec,
        ListTMSObjectsResponse.codec,
      )

      yield put(actionCreators.gettyListTMSObjectsSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyListTMSObjectFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listSitesSaga = gettyTakeLeading(
  'gettyListSitesRequest',
  function* () {
    try {
      const response: ListSitesResponse.Entity = yield* unaryGRPC(
        'mos.structure_access.StructureAccess/ListSites',
        {type: ListSitesRequest.refName},
        ListSitesRequest.codec,
        ListSitesResponse.codec,
      )

      yield put(actionCreators.gettyListSitesSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyCreateMoodFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listTourCategoriesSaga = gettyTakeLeading(
  'gettyListTourCategoriesRequest',
  function* listTourCategories(action: { payload: Site.Ref }) {
    try {

      const toursResponse: ListToursResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListTours',
        {...ListToursRequest.defaults},
        ListToursRequest.codec,
        ListToursResponse.codec,
      );

      const response: ListTourCategoriesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListTourCategories',
        {
          ...ListTourCategoriesRequest.defaults,
          filter: {
            ...ListTourCategoriesRequest.BySite.defaults,
            siteRefs: [ action.payload ]
          }
        },
        ListTourCategoriesRequest.codec,
        ListTourCategoriesResponse.codec,
      );
      yield put(actionCreators.gettyListTourCategoriesSuccess(response, toursResponse));

    } catch (error) {
      yield put(actionCreators.gettyListTourCategoriesFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const createTourCategorySaga = gettyTakeLeading(
  'gettyCreateTourCategoryRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: CreateTourCategoryResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateTourCategory',
        payload,
        CreateTourCategoryRequest.codec,
        CreateTourCategoryResponse.codec,
      )

      yield put(actionCreators.gettyCreateTourCategorySuccess(response))
      yield put(actionCreators.gettyCreateTourCategoryReset())

    } catch (error) {
      yield put(actionCreators.gettyCreateTourCategoryFailure(
        failureStatusMessage(error.message)
      ))
      yield put(actionCreators.gettyCreateTourCategoryReset())
    }
  }
)

const deleteTourCategorySaga = gettyTakeLeading(
  'gettyDeleteTourCategoryRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: DeleteTourCategoryResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteTourCategory',
        payload,
        DeleteTourCategoryRequest.codec,
        DeleteTourCategoryResponse.codec,
      )

      yield put(actionCreators.gettyDeleteTourCategorySuccess(response))
      yield put(actionCreators.gettyDeleteTourCategoryReset())

    } catch (error) {
        yield put(actionCreators.gettyDeleteTourCategoryFailure(
          failureStatusMessage(error.message)
        ))
        yield put(actionCreators.gettyDeleteTourCategoryReset())
    }
  }
)

const updateTourCategorySaga = gettyTakeLeading(
  'gettyUpdateTourCategoryRequest',
  function* (action) {
    try {
      const { payload, fieldName } = action
      const response: UpdateTourCategoryResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateTourCategory',
        payload,
        UpdateTourCategoryRequest.codec,
        UpdateTourCategoryResponse.codec,
      );
      yield put(actionCreators.gettyUpdateTourCategorySuccess(response, fieldName, TourCategory.mustRef(payload.tourCategoryRef),))
      yield put(actionCreators.gettyUpdateTourCategoryReset())
    } catch (error) {
      yield put(actionCreators.gettyUpdateTourCategoryFailure(
        failureStatusMessage(error.message)
      ))
      yield put(actionCreators.gettyUpdateTourCategoryReset())
    }
  }
);

const updateTourCategoriesSaga = gettyTakeLeading(
  'gettyUpdateTourCategoriesRequest',
  function* (action) {
    try {
      const { payload } = action

      const toursResponse: ListToursResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListTours',
        {...ListToursRequest.defaults},
        ListToursRequest.codec,
        ListToursResponse.codec,
      );

      const response: UpdateTourCategoriesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateTourCategories',
        payload,
        UpdateTourCategoriesRequest.codec,
        UpdateTourCategoriesResponse.codec,
      );

      yield put(actionCreators.gettyUpdateTourCategoriesSuccess(response, toursResponse));

    } catch (error) {
      yield put(actionCreators.gettyUpdateTourCategoriesFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const listCarouselItemsSaga = gettyTakeLeading(
  'gettyListCarouselItemsRequest',
  function* (action) {
    try {
      const carouselItemsResponse: ListCarouselItemsResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/ListCarouselItems',
        action.payload,
        ListCarouselItemsRequest.codec,
        ListCarouselItemsResponse.codec,
      );

      const mediaRefs = action.heroImageMedia ? [ ...getMediaRefs(carouselItemsResponse.carouselItems), action.heroImageMedia ] : getMediaRefs(carouselItemsResponse.carouselItems)

      const mediaResponse = mediaRefs.length ?
        yield* unaryGRPC(
          'mos.media_access.MediaAccess/ListMedia',
          {...ListMediaRequest.defaults,
            page: {
              ...Page.defaults,
              pageSize: 100,
            },
            filter: {
              ...ListMediaRequest.ByRef.defaults,
              mediaRefs: mediaRefs
            }
          },
          ListMediaRequest.codec,
          ListMediaResponse.codec,
        ) : undefined;

      const mediaList = mediaResponse && mediaResponse.media

      yield put(actionCreators.gettyListCarouselItemsSuccess(carouselItemsResponse.carouselItems, mediaList));

    } catch (error) {
      yield put(actionCreators.gettyListCarouselItemsFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

function getMediaRefs(carouselItems: ReadonlyArray<CarouselItem.Entity>) {
  const carouselItemValueItems = carouselItems.map(
    (carouselItem) => carouselItem.value && carouselItem.value.item && carouselItem.value.item
  )

  let mediaRefs: ReadonlyArray<Media.Ref> = []

  carouselItemValueItems.map((valueItem) => {
    if (valueItem) {
      switch (valueItem.type) {
        case 'mos.org.getty.audio.CarouselItemValue.AudioItem': {
          if (valueItem.audio) {
            mediaRefs = [...mediaRefs, (audioCarouselItemValueSchema.audio.read(valueItem.audio) as any as Media.Ref)]
          }
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.HeroImageItem': {
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.ImageItem': {
          if (valueItem.fullScreenImageRef) {
            mediaRefs = [...mediaRefs, (imageCarouselItemValueSchema.fullScreenImageRef.read(valueItem.fullScreenImageRef) as any as Media.Ref)]
          }
          break
        }
        case 'mos.org.getty.audio.CarouselItemValue.VideoItem': {
          if (valueItem.video) {
            mediaRefs = [...mediaRefs, (videoCarouselItemValueSchema.video.read(valueItem.video) as any as Media.Ref)]
          }
          break
        }
      }
    }
  })
  return mediaRefs;
}

const getCarouselItemSaga = gettyTakeLeading(
  'gettyGetCarouselItemRequest',
  function* (action) {
    try {
      const response: GetCarouselItemResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetCarouselItem',
        action.payload,
        GetCarouselItemRequest.codec,
        GetCarouselItemResponse.codec,
      );
      if (!response.carouselItem) {
        throw new Invariant('[ getCarouselItemSaga ] Missing `carouselItem` in response payload')
      }

        yield put(actionCreators.gettyGetCarouselItemSuccess(response));
    } catch (error) {
      yield put(actionCreators.gettyGetCarouselItemFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const createCarouselItemSaga = gettyTakeLeading(
  'gettyCreateCarouselItemRequest',
  function* (action) {
    try {
      const response: CreateCarouselItemResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateCarouselItem',
        action.payload,
        CreateCarouselItemRequest.codec,
        CreateCarouselItemResponse.codec,
      )

      yield put(actionCreators.gettyCreateCarouselItemSuccess(response))

      // if a carousel item is successfully created, update the stop with the new carousel item ref
      if (response && response.carouselItem && response.carouselItem.ref && action.stop) {
        try {
          const updateStopPayload: UpdateStopRequest.Entity = {
            ...UpdateStopRequest.defaults,
            stopRef: {
              typename: 'mos.org.getty.audio.Stop',
              id: Stop.mustRef(action.stop.ref).id,
            },
            updateMask: {
              type: "google.protobuf.FieldMask",
              fields: ['carouselItemRefs'],
            },
            carouselItemRefs: action.stop.carouselItemRefs ? [...action.stop.carouselItemRefs, response.carouselItem.ref] : [response.carouselItem.ref]
          }

          const stopResponse: UpdateStopResponse.Entity = yield* unaryGRPC(
            'mos.org.getty.audio_management.AudioManagement/UpdateStop',
            updateStopPayload,
            UpdateStopRequest.codec,
            UpdateStopResponse.codec,
          )

          if(!stopResponse.stop) {
            throw new Invariant('[ createCarouselItemSaga ] Missing `stop` in response payload')
          }
          yield put(actionCreators.gettyUpdateStopSuccess(stopResponse.stop))
          yield put(actionCreators.gettyUpdateStopReset())
        }
        catch (error) {
          yield put(actionCreators.gettyUpdateStopFailure(
            failureStatusMessage(error.message)
          ))
        }
      }
    } catch (error) {
      yield put(actionCreators.gettyCreateCarouselItemFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const updateCarouselItemSaga = gettyTakeLeading(
  'gettyUpdateCarouselItemRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: UpdateCarouselItemResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateCarouselItem',
        payload,
        UpdateCarouselItemRequest.codec,
        UpdateCarouselItemResponse.codec,
      );
      if(!response.carouselItem) {
        throw new Invariant('[ updateCarouselItemSaga ] Missing `carouselItem` in response payload')
      }

      yield put(actionCreators.gettyUpdateCarouselItemSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyUpdateCarouselItemFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const listVisitorInfoPagesSaga = gettyTakeLeading(
  'gettyListVisitorInfoPagesRequest',
  function* listVisitorInfoPages(action: { payload: Site.Ref }) {
    try {
      const { payload } = action

      const siteContentResponse: ListVisitorInfoSiteContentsResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_access.VisitorInfoAccess/ListVisitorInfoSiteContents',
        ListVisitorInfoSiteContentsRequest.defaults,
        ListVisitorInfoSiteContentsRequest.codec,
        ListVisitorInfoSiteContentsResponse.codec,
      )
      const siteContent = siteContentResponse && siteContentResponse.visitorInfoSiteContent.find(item => item.siteRef && item.siteRef.id === payload.id)
      if (!siteContent || !siteContent.ref) {
        throw new Invariant(`[ listVisitorInfoPagesSaga ] Missing siteContent.ref for ref "${payload.id}" in response payload`)
      }

      const pagesResponse: ListVisitorInfoPagesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_access.VisitorInfoAccess/ListVisitorInfoPages',
        {
          ...ListVisitorInfoPagesRequest.defaults,
          filter: {
            ...ListVisitorInfoPagesRequest.VisitorInfoPagesFilter.defaults,
            siteContentRef:[siteContent.ref]
          }
        },
        ListVisitorInfoPagesRequest.codec,
        ListVisitorInfoPagesResponse.codec,
      )

      const amenitiesResponse: ListAmenitiesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_access.VisitorInfoAccess/ListAmenities',
        ListAmenitiesRequest.defaults,
        ListAmenitiesRequest.codec,
        ListAmenitiesResponse.codec,
      )

      const pages = pagesResponse.visitorInfoPage.map(page => {
        return {
          page,
          amenities: (page.pageProperties && page.pageProperties.type === VisitorInfoPage.AmenityCategory.refName) ? amenitiesResponse.amenities.filter(amenity => amenity.amenityCategoryRef && page.ref && amenity.amenityCategoryRef.id === page.ref.id) : undefined }
      })

      yield put(actionCreators.gettyListVisitorInfoPagesSuccess({
        type: ListVisitorInfoPagesResponse.refName,
        pages,
        siteContentRef: siteContent.ref,
      }))

    } catch (error) {
      yield put(actionCreators.gettyListVisitorInfoPagesFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateVisitorInfoSiteContentSaga = gettyTakeLeading(
  'gettyUpdateVisitorInfoSiteContentRequest',
  function* (action) {

    try {
      const { pages, siteContentRef } = action.payload
      // Update all pages of type: Amenity Category with their current amenity page refs order.
      const visitorInfoAmenityCategoryPages = pages.filter(item => item.page.pageProperties && item.page.pageProperties.type === VisitorInfoPage.AmenityCategory.refName)
      yield all(
        visitorInfoAmenityCategoryPages
          .filter(isNotNullOrUndefined)
          .map(({ page, amenities }) => {
            const pageProperties = page.pageProperties as VisitorInfoPage.AmenityCategory.Entity
            return unaryGRPC(
              'mos.org.getty.visitor_info_management.VisitorInfoManagement/UpdateVisitorInfoPage',
              {
                ...UpdateVisitorInfoPageRequest.defaults,
                visitorInfoPageRef: VisitorInfoPage.mustRef(page.ref),
                updateMask: {
                  type: 'google.protobuf.FieldMask',
                  fields: [
                    'amenityCategory',
                  ]
                },
                listTitle: page.listTitle,
                listIconRef: page.listIconRef,
                pageProperties: {
                  ...VisitorInfoPage.AmenityCategory.defaults,
                  ...pageProperties,
                  amenityRefs: amenities && amenities.map(amenity => Amenity.mustRef(amenity.ref)) || [],
                }
              },
              UpdateVisitorInfoPageRequest.codec,
              UpdateVisitorInfoPageResponse.codec,
            )
          })
      )

      // Update parent pages order.
      const visitorInfoPageRefs = pages.map(({ page }) => VisitorInfoPage.mustRef(page.ref))
      const response: UpdateVisitorInfoSiteContentResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/UpdateVisitorInfoSiteContent',
        {
          ...UpdateVisitorInfoSiteContentRequest.defaults,
          visitorInfoSiteContentRef: siteContentRef,
          updateMask: {
            type: 'google.protobuf.FieldMask',
            fields: [
              'visitorInfoPageRefs',
            ]
          },
          visitorInfoPageRefs,
        },
        UpdateVisitorInfoSiteContentRequest.codec,
        UpdateVisitorInfoSiteContentResponse.codec,
      )

      yield put(actionCreators.gettyUpdateVisitorInfoSiteContentSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyUpdateVisitorInfoSiteContentFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const createVisitorInfoPageSaga = gettyTakeLeading(
  'gettyCreateVisitorInfoPageRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: CreateVisitorInfoPageResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/CreateVisitorInfoPage',
        payload,
        CreateVisitorInfoPageRequest.codec,
        CreateVisitorInfoPageResponse.codec,
      )

      yield put(actionCreators.gettyCreateVisitorInfoPageSuccess(response))
      yield put(actionCreators.gettyCreateVisitorInfoPageReset())

    } catch (error) {
      yield put(actionCreators.gettyCreateVisitorInfoPageFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const deleteVisitorInfoPageSaga = gettyTakeLeading(
  'gettyDeleteVisitorInfoPageRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: DeleteVisitorInfoPageResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/DeleteVisitorInfoPage',
        payload,
        DeleteVisitorInfoPageRequest.codec,
        DeleteVisitorInfoPageResponse.codec,
      )

      yield put(actionCreators.gettyDeleteVisitorInfoPageSuccess(response))
      yield put(actionCreators.gettyDeleteVisitorInfoPageReset())

    } catch (error) {
      yield put(actionCreators.gettyDeleteVisitorInfoPageFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const deleteAmenitySaga = gettyTakeLeading(
  'gettyDeleteAmenityRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: DeleteAmenityResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/DeleteAmenity',
        payload,
        DeleteAmenityRequest.codec,
        DeleteAmenityResponse.codec,
      )

      yield put(actionCreators.gettyDeleteAmenitySuccess(response))
      yield put(actionCreators.gettyDeleteAmenityReset())

    } catch (error) {
      yield put(actionCreators.gettyDeleteAmenityFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const createAmenitySaga = gettyTakeLeading(
  'gettyCreateAmenityRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: CreateAmenityResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/CreateAmenity',
        payload,
        CreateAmenityRequest.codec,
        CreateAmenityResponse.codec,
      )

      yield put(actionCreators.gettyCreateAmenitySuccess(response))
      yield put(actionCreators.gettyCreateAmenityReset())

    } catch (error) {
      yield put(actionCreators.gettyCreateAmenityFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

// when a top level visitor info page is passed this will return any BuildingLevel.Refs and
// VisitorInfoPage.SiteMap refs which belong to the same Site.
function* getAmenityWayfindingOptions(pageRef: VisitorInfoPage.Ref) {
  // there is no reverse reference to the site of this entity so we need to look up all the
  // container entities first, then try to locate the Site for the pageRef.
  const siteContentResponse: ListVisitorInfoSiteContentsResponse.Entity = yield* unaryGRPC(
    'mos.org.getty.visitor_info_access.VisitorInfoAccess/ListVisitorInfoSiteContents',
    ListVisitorInfoSiteContentsRequest.defaults,
    ListVisitorInfoSiteContentsRequest.codec,
    ListVisitorInfoSiteContentsResponse.codec,
  )

  // identify the site ref.
  let siteRef: Site.Ref | undefined = undefined
  siteContentResponse.visitorInfoSiteContent.map(site => {
    const match = site.visitorInfoPageRefs.find(page => pageRef.id === page.id)

    if (match) {
      siteRef = site.siteRef
    }
  })

  // find ref options for wayfinding values based on this site.
  if(Site.isRef(siteRef)) {
    const listWayfindingMapsResponse: ListWayfindingMapsResponse.Entity = yield* unaryGRPC(
      'mos.org.getty.visitor_info_access.VisitorInfoAccess/ListWayfindingMaps',
      {
        ...ListWayfindingMapsRequest.defaults,
        filter: {
          ...ListWayfindingMapsRequest.BySite.defaults,
          siteRefs: [Site.mustRef(siteRef)]
        }
      },
      ListWayfindingMapsRequest.codec,
      ListWayfindingMapsResponse.codec,
    )

    const wayfindingMaps: AmentityUpdate['wayfindingMaps'] = listWayfindingMapsResponse.wayfindingMaps

    return {
      wayfindingMaps,
    }
  }

  return undefined
}

const getAmenitySaga = gettyTakeLeading(
  'gettyGetAmenityRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: GetAmenityResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_access.VisitorInfoAccess/GetAmenity',
        payload,
        GetAmenityRequest.codec,
        GetAmenityResponse.codec,
      )

      if(!response.amenity) {
        throw new Invariant('No amenity provided in payload response.')
      }

      const wayfindingOptions = yield* getAmenityWayfindingOptions(VisitorInfoPage.mustRef(response.amenity.amenityCategoryRef))

      yield put(actionCreators.gettyGetAmenitySuccess({
        amenity: response.amenity,
        ...wayfindingOptions,
      }))

    } catch (error) {
      yield put(actionCreators.gettyGetAmenityFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateAmenitySaga = gettyTakeLeading(
  'gettyUpdateAmenityRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: UpdateAmenityResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/UpdateAmenity',
        payload,
        UpdateAmenityRequest.codec,
        UpdateAmenityResponse.codec,
      )

      if(!response.amenity) {
        throw new Invariant('No amenity provided in payload response.')
      }

      const wayfindingOptions = yield* getAmenityWayfindingOptions(VisitorInfoPage.mustRef(response.amenity.amenityCategoryRef))

      yield put(actionCreators.gettyUpdateAmenitySuccess({
        amenity: response.amenity,
        ...wayfindingOptions,
      }))

    } catch (error) {
      yield put(actionCreators.gettyUpdateAmenityFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const getVisitorInfoPageSaga = gettyTakeLeading(
  'gettyGetVisitorInfoPageRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: GetVisitorInfoPageResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_access.VisitorInfoAccess/GetVisitorInfoPage',
        payload,
        GetVisitorInfoPageRequest.codec,
        GetVisitorInfoPageResponse.codec,
      )

      yield put(actionCreators.gettyGetVisitorInfoPageSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyGetVisitorInfoPageFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateVisitorInfoPageSaga = gettyTakeLeading(
  'gettyUpdateVisitorInfoPageRequest',
  function* (action) {

    try {
      const { payload } = action
      const response: UpdateVisitorInfoPageResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.visitor_info_management.VisitorInfoManagement/UpdateVisitorInfoPage',
        payload,
        UpdateVisitorInfoPageRequest.codec,
        UpdateVisitorInfoPageResponse.codec,
      )

      yield put(actionCreators.gettyUpdateVisitorInfoPageSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyUpdateVisitorInfoPageFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const getAnalyticsSaga = gettyTakeLeading(
  'gettyGetAnalyticsRequest',
  function* () {
    try {
      let result: Response
      try {
        result = yield call(fetch, `${bffEndpoint(window.location.hostname)}/analytics`, assemblePayload({}))
      } catch (error) {
        throw new Error(`[ getAnalyticsSaga ] There was a network error: ${error.message}.`)
      }

      // check for errors in the response.
      if (!result || !result.ok) {
        // @ts-ignore
        const response = yield call([result, 'json'])
        if (!response) {
          throw new Error('[ getAnalyticsSaga ] There was an error with receiving an expected response.')
        }
        // check for invalid access token.
        else if (response.error && result && result.status === 403) {
          // refresh access token or queue request until a new token is obtained.
          yield call(ensureValidSession)

          // once ready, retry the network request a second and final time.
          try {
            result = yield call(fetch, `${bffEndpoint(window.location.hostname)}/analytics`, assemblePayload({}))
          } catch (error) {
            throw new Error(`[ getAnalyticsSaga ] There was a network error: ${error.message}.`)
          }

          if (!result || !result.ok) {
            throw new Error('[ getAnalyticsSaga ] There was an error with the response.')
          }

        } else {
          throw new Invariant('[ getAnalyticsSaga ] Unhandled response.')
        }
      }

      // @ts-ignore
      const response = yield call([result, 'json'])
      yield put(actionCreators.gettyGetAnalyticsSuccess(response.body as AnalyticsResponse))

    } catch (error) {
      yield put(actionCreators.gettyGetAnalyticsFailure(
        failureStatusMessage(error.message)
        ))
      }
    }
  )
  const listBundleSummariesSaga = gettyTakeLeading(
  'gettyGetListBundleSummariesRequest',
  function* () {
    try {
      const response: ListBundleSummariesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.publish_access.PublishAccess/ListBundleSummaries',
        ListBundleSummariesRequest.defaults,
        ListBundleSummariesRequest.codec,
        ListBundleSummariesResponse.codec,
      )

      yield put(actionCreators.gettyGetListBundleSummariesSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyGetListBundleSummariesFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listBundlesSaga = gettyTakeLeading(
  'gettyGetListBundlesRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: ListBundlesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.publish_access.PublishAccess/ListBundles',
        payload,
        ListBundlesRequest.codec,
        ListBundlesResponse.codec,
      )
      yield put(actionCreators.gettyGetListBundlesSuccess(response))
    } catch (error) {
      captureException({ error })
    }
  }
)

const listReleasesSaga = gettyTakeLeading(
  'gettyGetListReleasesRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: ListReleasesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.publish_access.PublishAccess/ListReleases',
        payload,
        ListReleasesRequest.codec,
        ListReleasesResponse.codec,
      )

      yield put(actionCreators.gettyGetListReleasesSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyGetListReleasesFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const createReleaseSaga = gettyTakeLeading(
  'gettyCreateReleaseRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: CreateReleaseResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.publish_management.PublishManagement/CreateRelease',
        payload,
        CreateReleaseRequest.codec,
        CreateReleaseResponse.codec,
      )

      yield put(actionCreators.gettyCreateReleaseSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyCreateReleaseFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listValidationFailuresSaga = gettyTakeLeading(
  'gettyListValidationFailuresRequest',
  function* (action) {
    try {
      const { payload } = action
      const response: ListValidationFailuresResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.validation_access.ValidationAccess/ListValidationFailures',
        payload,
        ListValidationFailuresRequest.codec,
        ListValidationFailuresResponse.codec,
      )

      yield put(actionCreators.gettyListValidationFailuresSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyListValidationFailuresFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listAllValidationFailuresSaga = gettyTakeLeading(
  'gettyListAllValidationFailuresRequest',
  function* (action) {
    try {
      const { payload } = action
      const bundleSummariesResponse: ListBundleSummariesResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.publish_access.PublishAccess/ListBundleSummaries',
        ListBundleSummariesRequest.defaults,
        ListBundleSummariesRequest.codec,
        ListBundleSummariesResponse.codec,
      )
      const response: ListValidationFailuresResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.validation_access.ValidationAccess/ListValidationFailures',
        payload,
        ListValidationFailuresRequest.codec,
        ListValidationFailuresResponse.codec,
      )

      yield put(actionCreators.gettyListAllValidationFailuresSuccess({
        failures: response.failures,
        summaries: bundleSummariesResponse.summaries,
      }))
    } catch (error) {
      yield put(actionCreators.gettyListValidationFailuresFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const listGetty360EventsSaga = gettyTakeLeading(
  'gettyListGetty360EventsRequest',
  function* () {
    try {
      const response: ListGetty360EventsResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.integration.IntegrationAccess/ListGetty360Events',
        ListGetty360EventsRequest.defaults,
        ListGetty360EventsRequest.codec,
        ListGetty360EventsResponse.codec,
      )

      yield put(actionCreators.gettyListGetty360EventsSuccess(response))

    } catch (error) {
      yield put(actionCreators.gettyListGetty360EventsFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

function* getStopPointsOfInterestByRef(stopPointsOfInterestRefs: ReadonlyArray<StopPointOfInterest.Ref>) {
  if(!stopPointsOfInterestRefs) return

  if (!stopPointsOfInterestRefs.length) return [];
  const payload: ListStopPointsOfInterestRequest.Entity = {
    ...ListStopPointsOfInterestRequest.defaults,
    filter: {
      ...ListStopPointsOfInterestRequest.ByRef.defaults,
      stopPointsOfInterestRefs: stopPointsOfInterestRefs,
    },
  };
  const response: ListStopPointsOfInterestResponse.Entity = yield* unaryGRPC(
    'mos.org.getty.audio_access.AudioAccess/ListStopPointsOfInterest',
    payload,
    ListStopPointsOfInterestRequest.codec,
    ListStopPointsOfInterestResponse.codec,
  );

  // reorder output to ensure that the order matches the input
  const stopIds = stopPointsOfInterestRefs.map(ref => ref.id);
  const stopPointsOfInterest = response.stopPointsOfInterest.slice().sort((requestItem, responseItem) =>
    stopIds.indexOf(requestItem.ref ? requestItem.ref.id : '') - stopIds.indexOf(responseItem.ref ? responseItem.ref.id : '')
  );

  const resolvedStops: ReadonlyArray<Stop.Entity> =
    yield* resolveAttributeRefsOfEntityList<Stop.Entity>(stopPointsOfInterest.map(({stop}) => stop).filter((stop) => stop) as Array<Stop.Entity>, stopSchema)

  return stopPointsOfInterest.map((stopPointOfInterest) => {
    const resolvedStop = resolvedStops.find((stop) => stop.ref && stopPointOfInterest.stop && stopPointOfInterest.stop.ref && stop.ref.id == stopPointOfInterest.stop.ref.id);
    return resolvedStop ? { ...stopPointOfInterest, stop: resolvedStop } : stopPointOfInterest
  });
}

function* getGenericPointsOfInterestByRef(genericPointsOfInterestRefs: ReadonlyArray<GenericPointOfInterest.Ref>) {
  if(!genericPointsOfInterestRefs) return

  if (!genericPointsOfInterestRefs.length) return [];
  const payload: ListGenericPointsOfInterestRequest.Entity = {
    ...ListGenericPointsOfInterestRequest.defaults,
    filter: {
      ...ListGenericPointsOfInterestRequest.ByRef.defaults,
      genericPointsOfInterestRefs: genericPointsOfInterestRefs,
    },
  };
  const response: ListGenericPointsOfInterestResponse.Entity = yield* unaryGRPC(
    'mos.org.getty.audio_access.AudioAccess/ListGenericPointsOfInterest',
    payload,
    ListGenericPointsOfInterestRequest.codec,
    ListGenericPointsOfInterestResponse.codec,
  );

  // reorder output to ensure that the order matches the input
  const stopIds = genericPointsOfInterestRefs.map(ref => ref.id);
  const genericPointsOfInterest = response.genericPointsOfInterest.slice().sort((requestItem, responseItem) =>
    stopIds.indexOf(requestItem.ref ? requestItem.ref.id : '') - stopIds.indexOf(responseItem.ref ? responseItem.ref.id : '')
  );

  return genericPointsOfInterest;
}

function* getEventPointsOfInterestByRef(eventPointsOfInterestRefs: ReadonlyArray<EventPointOfInterest.Ref>) {
  if(!eventPointsOfInterestRefs) return

  if (!eventPointsOfInterestRefs.length) return [];
  const payload: ListEventPointsOfInterestRequest.Entity = {
    ...ListEventPointsOfInterestRequest.defaults,
    filter: {
      ...ListEventPointsOfInterestRequest.ByRef.defaults,
      eventPointsOfInterestRefs: eventPointsOfInterestRefs,
    },
  };
  const response: ListEventPointsOfInterestResponse.Entity = yield* unaryGRPC(
    'mos.org.getty.audio_access.AudioAccess/ListEventPointsOfInterest',
    payload,
    ListEventPointsOfInterestRequest.codec,
    ListEventPointsOfInterestResponse.codec,
  );

  // reorder output to ensure that the order matches the input
  const stopIds = eventPointsOfInterestRefs.map(ref => ref.id);
  const eventPointsOfInterest = response.eventPointsOfInterest.slice().sort((requestItem, responseItem) =>
    stopIds.indexOf(requestItem.ref ? requestItem.ref.id : '') - stopIds.indexOf(responseItem.ref ? responseItem.ref.id : '')
  );

  return eventPointsOfInterest;
}

const getStopPointOfInterestSaga = gettyTakeLeading(
  'gettyGetStopPointOfInterestRequest',
  function* (action) {
    try {
      let stopMoods: ListMoodsResponse.Entity | undefined
      let stopTours: ListToursResponse.Entity | undefined
      const response: GetStopPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetStopPointOfInterest',
        action.payload,
        GetStopPointOfInterestRequest.codec,
        GetStopPointOfInterestResponse.codec,
      );
      if (!response.stopPointOfInterest) {
        throw new Invariant('[ getStopPointOfInterestSaga ] Missing `stopPointOfInterest` in response payload')
      }
      if (!response.stopPointOfInterest.stop) {
        throw new Invariant('[ getStopPointOfInterestSaga ] Missing `stop` in response payload')
      }

      // get moods which contain the stop.
      if(response.stopPointOfInterest.stop.moodRefs) {
        stopMoods = yield* unaryGRPC(
          'mos.org.getty.audio_access.AudioAccess/ListMoods',
          {
            ...ListMoodsRequest.defaults,
            filter: {
              type: 'mos.org.getty.audio_access.ListMoodsRequest.ByRef' as const,
              moodRefs: response.stopPointOfInterest.stop.moodRefs,
            }
          },
          ListMoodsRequest.codec,
          ListMoodsResponse.codec,
        )
      }
      if(response.stopPointOfInterest.stop.tourRefs) {
        stopTours = yield* unaryGRPC(
          'mos.org.getty.audio_access.AudioAccess/ListTours',
          {
            ...ListToursRequest.defaults,
            filter: {
              type: 'mos.org.getty.audio_access.ListToursRequest.ByRef' as const,
              tourRefs: response.stopPointOfInterest.stop.tourRefs,
            }
          },
          ListToursRequest.codec,
          ListToursResponse.codec,
        )
      }

      if (response.stopPointOfInterest.stop.basedOnRef) {
        let baseEntity: GetTMSObjectResponse.Entity | undefined

        const { basedOnRef } = response.stopPointOfInterest.stop
        // check based on entity type.
        if (basedOnRef.typename === 'mos.org.getty.integration_tms.TMSObject') {
          // get the tms object the stop was based on.
          baseEntity = yield* unaryGRPC(
            'mos.org.getty.integration_tms.IntegrationTMSAccess/GetTMSObject',
            {
              type: 'mos.org.getty.integration_tms.GetTMSObjectRequest',
              tmsObjectRef: {
                typename: 'mos.org.getty.integration_tms.TMSObject',
                id: basedOnRef.id,
              }
            },
            GetTMSObjectRequest.codec,
            GetTMSObjectResponse.codec,
          )
        }

        // get field mappings available to the entity type.
        const fieldMappings: GetLinkedFieldMappingsResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_mappings.LinkedFieldMappings/GetLinkedFieldMappings',
          {
            type: 'mos.linked_field_mappings.GetLinkedFieldMappingsRequest',
            sourceTypename: 'mos.org.getty.audio.Stop' as const,
            targetTypename: basedOnRef.typename,
            relationshipName: 'based_on',
            sourceAttributeNames: extractSourceAttributeNames(Stop.defaults),
          },
          GetLinkedFieldMappingsRequest.codec,
          GetLinkedFieldMappingsResponse.codec,
        )

        // only store valid mappings.
        const mappings = fieldMappings.mappings.filter(mapping => mapping.targetAttributeNames.length)

        const targetFieldRefs = mappings.reduce((acc: ReadonlyArray<any>, mapping) => {
          const attributeRefs: ReadonlyArray<AttributeRef.Entity> = mapping.targetAttributeNames
            .map(attributeName => ({
              type: 'mos.entity.AttributeRef',
              typename: basedOnRef.typename,
              id: basedOnRef.id,
              attributeName,
            }))

          return [
            ...acc,
            ...attributeRefs,
          ]
        }, [])

        // resolve attribute refs.
        const resolvedValues: ResolveLinkableFieldValuesResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_resolver.LinkedFieldResolver/ResolveLinkableFieldValues',
          {
            type: ResolveLinkableFieldValuesRequest.refName,
            targetFieldRefs,
          },
          ResolveLinkableFieldValuesRequest.codec,
          ResolveLinkableFieldValuesResponse.codec,
        )

        yield put(actionCreators.gettyGetStopPointOfInterestSuccess(
          response.stopPointOfInterest,
          resolvedValues.resolvedValueResults,
          baseEntity && baseEntity.tmsObject || undefined,
          stopMoods && stopMoods.moods || undefined,
          stopTours && stopTours.tours || undefined,
        ))
      } else {
        yield put(actionCreators.gettyGetStopPointOfInterestSuccess(
          response.stopPointOfInterest,
        ))
      }
    } catch (error) {
      yield put(actionCreators.gettyGetStopPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const createStopPointOfInterestSaga = gettyTakeLeading(
  'gettyCreateStopPointOfInterestRequest',
  function* (action) {
    try {
      const { payload } = action

      if (!payload.moodRef) {
        throw new Invariant('[ createStopPointOfInterestSaga ] Missing `moodRef` in payload')
      }
      if (!payload.stopRef) {
        throw new Invariant('[ createStopPointOfInterestSaga ] Missing `stopRef` in payload')
      }

      const response: CreateStopPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateStopPointOfInterest',
        { ...CreateStopPointOfInterestRequest.defaults, moodRef: payload.moodRef, stopRef: payload.stopRef },
        CreateStopPointOfInterestRequest.codec,
        CreateStopPointOfInterestResponse.codec,
      )


      yield put(actionCreators.gettyCreateStopPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyCreateStopPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateStopPointOfInterestSaga = gettyTakeLeading(
  'gettyUpdateStopPointOfInterestRequest',
  function* (action: GettyActions['gettyUpdateStopPointOfInterestRequest']) {
    try {
      const { payload } = action
      const response: UpdateStopPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateStopPointOfInterest',
        payload,
        UpdateStopPointOfInterestRequest.codec,
        UpdateStopPointOfInterestResponse.codec,
      );
      if (!response.stopPointOfInterest) {
        throw new Invariant('[ updateStopPointOfInterestSaga ] Missing `stopPointOfInterest` in response payload')
      }
      if (!response.stopPointOfInterest.stop) {
        throw new Invariant('[ updateStopPointOfInterestSaga ] Missing `stopPointOfInterest.stop` in response payload')
      }

      if (response.stopPointOfInterest.stop.basedOnRef) {
        let baseEntity: GetTMSObjectResponse.Entity | undefined

        const { basedOnRef } = response.stopPointOfInterest.stop
        // check based on entity type.
        if (basedOnRef.typename === 'mos.org.getty.integration_tms.TMSObject') {
          // get the tms object the stop was based on.
          baseEntity = yield* unaryGRPC(
            'mos.org.getty.integration_tms.IntegrationTMSAccess/GetTMSObject',
            {
              type: 'mos.org.getty.integration_tms.GetTMSObjectRequest',
              tmsObjectRef: {
                typename: 'mos.org.getty.integration_tms.TMSObject',
                id: basedOnRef.id,
              }
            },
            GetTMSObjectRequest.codec,
            GetTMSObjectResponse.codec,
          )
        }

        // get field mappings available to the entity type.
        const fieldMappings: GetLinkedFieldMappingsResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_mappings.LinkedFieldMappings/GetLinkedFieldMappings',
          {
            type: 'mos.linked_field_mappings.GetLinkedFieldMappingsRequest',
            sourceTypename: 'mos.org.getty.audio.Stop' as const,
            targetTypename: basedOnRef.typename,
            relationshipName: 'based_on',
            sourceAttributeNames: extractSourceAttributeNames(Stop.defaults),
          },
          GetLinkedFieldMappingsRequest.codec,
          GetLinkedFieldMappingsResponse.codec,
        )

        // only store valid mappings.
        const mappings = fieldMappings.mappings.filter(mapping => mapping.targetAttributeNames.length)

        const targetFieldRefs = mappings.reduce((acc: ReadonlyArray<any>, mapping) => {
          const attributeRefs: ReadonlyArray<AttributeRef.Entity> = mapping.targetAttributeNames
            .map(attributeName => ({
              type: 'mos.entity.AttributeRef',
              typename: basedOnRef.typename,
              id: basedOnRef.id,
              attributeName,
            }))

          return [
            ...acc,
            ...attributeRefs,
          ]
        }, [])

        // resolve attribute refs.
        const resolvedValues: ResolveLinkableFieldValuesResponse.Entity = yield* unaryGRPC(
          'mos.linked_field_resolver.LinkedFieldResolver/ResolveLinkableFieldValues',
          {
            type: ResolveLinkableFieldValuesRequest.refName,
            targetFieldRefs,
          },
          ResolveLinkableFieldValuesRequest.codec,
          ResolveLinkableFieldValuesResponse.codec,
        )

        yield put(actionCreators.gettyUpdateStopPointOfInterestSuccess(response.stopPointOfInterest, resolvedValues.resolvedValueResults));
      } else {
        yield put(actionCreators.gettyUpdateStopPointOfInterestSuccess(response.stopPointOfInterest));
      }
    } catch (error) {
      yield put(actionCreators.gettyUpdateStopPointOfInterestFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const deleteStopPointOfInterestSaga = gettyTakeLeading(
  'gettyDeleteStopPointOfInterestRequest',
  function* (action: GettyActions['gettyDeleteStopPointOfInterestRequest']) {
    const { payload } = action
    try {
      const response: DeleteStopPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteStopPointOfInterest',
        payload,
        DeleteStopPointOfInterestRequest.codec,
        DeleteStopPointOfInterestResponse.codec,
      )

      yield put(actionCreators.gettyDeleteStopPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyDeleteStopPointOfInterestFailure(
        StopPointOfInterest.mustRef(payload.stopPointOfInterestRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

const getEventPointOfInterestSaga = gettyTakeLeading(
  'gettyGetEventPointOfInterestRequest',
  function* (action) {
    try {
      const response: GetEventPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetEventPointOfInterest',
        action.payload,
        GetEventPointOfInterestRequest.codec,
        GetEventPointOfInterestResponse.codec,
      );
      if (!response.eventPointOfInterest) {
        throw new Invariant('[ getEventPointOfInterestSaga ] Missing `eventPointOfInterest` in response payload')
      }
      if (!response.eventPointOfInterest.event) {
        throw new Invariant('[ getEventPointOfInterestSaga ] Missing `event` in response payload')
      }
      yield put(actionCreators.gettyGetEventPointOfInterestSuccess(
        response.eventPointOfInterest,
      ))
    } catch (error) {
      yield put(actionCreators.gettyGetEventPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const createEventPointOfInterestSaga = gettyTakeLeading(
  'gettyCreateEventPointOfInterestRequest',
  function* (action) {
    try {
      const { payload } = action

      if (!payload.moodRef) {
        throw new Invariant('[ createEventPointOfInterestSaga ] Missing `moodRef` in payload')
      }
      if (!payload.eventRef) {
        throw new Invariant('[ createEventPointOfInterestSaga ] Missing `eventRef` in payload')
      }

      const response: CreateEventPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateEventPointOfInterest',
        { ...CreateEventPointOfInterestRequest.defaults, moodRef: payload.moodRef, eventRef: payload.eventRef },
        CreateEventPointOfInterestRequest.codec,
        CreateEventPointOfInterestResponse.codec,
      )


      yield put(actionCreators.gettyCreateEventPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyCreateEventPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)


const updateEventPointOfInterestSaga = gettyTakeLeading(
  'gettyUpdateEventPointOfInterestRequest',
  function* (action: GettyActions['gettyUpdateEventPointOfInterestRequest']) {
    try {
      const { payload } = action
      const response: UpdateEventPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateEventPointOfInterest',
        payload,
        UpdateEventPointOfInterestRequest.codec,
        UpdateEventPointOfInterestResponse.codec,
      );
      if (!response.eventPointOfInterest) {
        throw new Invariant('[ updateEventPointOfInterestSaga ] Missing `eventPointOfInterest` in response payload')
      }
      const responseWithEvents: GettyUpdateEventPointOfInterestSuccess = {
        ...response,
        eventPointOfInterest: { ...response.eventPointOfInterest },
      }

      yield put(actionCreators.gettyUpdateEventPointOfInterestSuccess(responseWithEvents));
    } catch (error) {
      yield put(actionCreators.gettyUpdateEventPointOfInterestFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const deleteEventPointOfInterestSaga = gettyTakeLeading(
  'gettyDeleteEventPointOfInterestRequest',
  function* (action: GettyActions['gettyDeleteEventPointOfInterestRequest']) {
    const { payload } = action
    try {
      const response: DeleteEventPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteEventPointOfInterest',
        payload,
        DeleteEventPointOfInterestRequest.codec,
        DeleteEventPointOfInterestResponse.codec,
      )

      yield put(actionCreators.gettyDeleteEventPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyDeleteEventPointOfInterestFailure(
        EventPointOfInterest.mustRef(payload.eventPointOfInterestRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

const getGenericPointOfInterestSaga = gettyTakeLeading(
  'gettyGetGenericPointOfInterestRequest',
  function* (action) {
    try {
      const response: GetGenericPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_access.AudioAccess/GetGenericPointOfInterest',
        action.payload,
        GetGenericPointOfInterestRequest.codec,
        GetGenericPointOfInterestResponse.codec,
      );
      if (!response.genericPointOfInterest) {
        throw new Invariant('[ getGenericPointOfInterestSaga ] Missing `genericPointOfInterest` in response payload')
      }
      yield put(actionCreators.gettyGetGenericPointOfInterestSuccess(
        response.genericPointOfInterest,
      ))
    } catch (error) {
      yield put(actionCreators.gettyGetGenericPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
);

const createGenericPointOfInterestSaga = gettyTakeLeading(
  'gettyCreateGenericPointOfInterestRequest',
  function* (action) {
    try {
      const { payload } = action

      if (!payload.moodRef) {
        throw new Invariant('[ createGenericPointOfInterestSaga ] Missing `moodRef` in payload')
      }

      if (!payload.title) {
        throw new Invariant('[ createGenericPointOfInterestSaga ] Missing `title` in payload')
      }

      if (!payload.category) {
        throw new Invariant('[ createGenericPointOfInterestSaga ] Missing `category` in payload')
      }

      const response: CreateGenericPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/CreateGenericPointOfInterest',
        { ...CreateGenericPointOfInterestRequest.defaults, moodRef: payload.moodRef, title: payload.title, category: payload.category },
        CreateGenericPointOfInterestRequest.codec,
        CreateGenericPointOfInterestResponse.codec,
      )

      yield put(actionCreators.gettyCreateGenericPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyCreateGenericPointOfInterestFailure(
        failureStatusMessage(error.message)
      ))
    }
  }
)

const updateGenericPointOfInterestSaga = gettyTakeLeading(
  'gettyUpdateGenericPointOfInterestRequest',
  function* (action: GettyActions['gettyUpdateGenericPointOfInterestRequest']) {
    try {
      const { payload } = action
      const response: UpdateGenericPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/UpdateGenericPointOfInterest',
        payload,
        UpdateGenericPointOfInterestRequest.codec,
        UpdateGenericPointOfInterestResponse.codec,
      );
      if (!response.genericPointOfInterest) {
        throw new Invariant('[ updateGenericPointOfInterestSaga ] Missing `genericPointOfInterest` in response payload')
      }

      yield put(actionCreators.gettyUpdateGenericPointOfInterestSuccess(response.genericPointOfInterest));
    } catch (error) {
      yield put(actionCreators.gettyUpdateGenericPointOfInterestFailure(
        failureStatusMessage(error.message)
      ));
    }
  }
);

const deleteGenericPointOfInterestSaga = gettyTakeLeading(
  'gettyDeleteGenericPointOfInterestRequest',
  function* (action: GettyActions['gettyDeleteGenericPointOfInterestRequest']) {
    const { payload } = action
    try {
      const response: DeleteGenericPointOfInterestResponse.Entity = yield* unaryGRPC(
        'mos.org.getty.audio_management.AudioManagement/DeleteGenericPointOfInterest',
        payload,
        DeleteGenericPointOfInterestRequest.codec,
        DeleteGenericPointOfInterestResponse.codec,
      )

      yield put(actionCreators.gettyDeleteGenericPointOfInterestSuccess(response))
    } catch (error) {
      yield put(actionCreators.gettyDeleteGenericPointOfInterestFailure(
        GenericPointOfInterest.mustRef(payload.genericPointOfInterestRef),
        failureStatusMessage(error.message)
      ))
    }
  }
)

export const gettyRootSaga: ReadonlyArray<Effect> = [
  getStopSaga,
  listSitesSaga,
  listStopsSaga,
  listStopsSagaLatest,
  createTourSaga,
  createMoodSaga,
  createStopSaga,
  getTourSaga,
  listToursSaga,
  deleteTourSaga,
  getMoodSaga,
  listMoodsSaga,
  listVisitorTypesSaga,
  getVisitorTypeSaga,
  createVisitorTypeSaga,
  updateVisitorTypeSaga,
  deleteVisitorTypeSaga,
  deleteMoodSaga,
  updateStopSaga,
  updateTourSaga,
  updateMoodSaga,
  deleteStopSaga,
  listTMSObjectsSaga,
  listTourCategoriesSaga,
  createTourCategorySaga,
  deleteTourCategorySaga,
  updateTourCategorySaga,
  updateTourCategoriesSaga,
  listCarouselItemsSaga,
  getCarouselItemSaga,
  createCarouselItemSaga,
  updateCarouselItemSaga,
  listVisitorInfoPagesSaga,
  createVisitorInfoPageSaga,
  deleteVisitorInfoPageSaga,
  deleteAmenitySaga,
  createAmenitySaga,
  getVisitorInfoPageSaga,
  updateVisitorInfoPageSaga,
  updateAmenitySaga,
  getAmenitySaga,
  updateVisitorInfoSiteContentSaga,
  getAnalyticsSaga,
  listBundleSummariesSaga,
  listReleasesSaga,
  createReleaseSaga,
  listBundlesSaga,
  listValidationFailuresSaga,
  listAllValidationFailuresSaga,
  listGetty360EventsSaga,
  getStopPointOfInterestSaga,
  createStopPointOfInterestSaga,
  updateStopPointOfInterestSaga,
  deleteStopPointOfInterestSaga,
  getGenericPointOfInterestSaga,
  createGenericPointOfInterestSaga,
  updateGenericPointOfInterestSaga,
  deleteGenericPointOfInterestSaga,
  getEventPointOfInterestSaga,
  createEventPointOfInterestSaga,
  updateEventPointOfInterestSaga,
  deleteEventPointOfInterestSaga,
]
